import { APP_BASE_HREF, PlatformLocation, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import esMXLocale from '@angular/common/locales/es-MX';
import zhLocale from '@angular/common/locales/zh';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig
} from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleModule } from '@ng-idle/core';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { STORAGE_ENGINE, StorageOption, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store } from '@ngxs/store';
import { provideNgxMask } from 'ngx-mask';
import { environment } from 'src/environments/environment';
import {
  AgentState,
  AppConfigModel,
  AppConfigState,
  ApplicationState,
  AuthRequestInterceptor,
  AuthResponseInterceptor,
  BlueButtonMedicineCabinetState,
  CacheInterceptor,
  ConfirmationState,
  DeepLinkingParamsState,
  DemographicState,
  EnrollmentState,
  GoogleTagManagerState,
  GuidedHelpState,
  HelpfulToolsState,
  InitializePlanTypeDataMap,
  JsonAnalyticsTagManagerState,
  LanguageInterceptor,
  LiveEditState,
  LoginAgentIdInterceptor,
  MapState,
  MedicineCabinetState,
  MedSupState,
  MemberState,
  MillimanState,
  PharmacyState,
  PlansState,
  PlansUtilityService,
  PlanTypeAbbreviation,
  PlanYearInterceptor,
  PlanYearResourceService,
  PreferencesState,
  ProfileState,
  ProviderState,
  QuoteBeneficiaryState,
  Resource,
  RouteOptionsService,
  RuntimeState,
  ShoppingCartState,
  SiteState,
  SOAState,
  TabData,
  TwilioState,
  UiState,
  VnextSharedModule
} from 'vnext-shared';
import config from '../assets/config.json';
import { DEFAULT_ROUTES, IN_MEMORY_SCROLLING_OPTIONS, ROUTER_CONFIG_OPTIONS } from './app-routing.config';
import { CoreModule } from './core/core.module';
import { RouteBuilder } from './core/routes/route-generator';
import { VnextRouteReuseStrategy } from './core/routes/strategies/vnext-route-reuse.strategy';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { withVnextStorageLoggerPlugin } from './core/store/logger/vnext-logger-plugin/vnext-logger.plugin';
import { NGXS_CONFIG } from './core/store/ngxs.config';
import { VNextStorageEngine } from './core/store/vnext-storage-engine';
import { SharedModule } from './shared/shared.module';

// Register locale data
registerLocaleData(esMXLocale);
registerLocaleData(zhLocale);

// Load VnextSharedModule config
VnextSharedModule.loadConfig(environment, config);

function initializeAppRoutesAndSelectedPlanYear(): void {
  const router = inject(Router);
  const routeOptionsService = inject(RouteOptionsService);
  const planYearResourceService = inject(PlanYearResourceService);

  const routes = router.config;
  const appConfig = (environment.liveedit ? {} : config) as AppConfigModel;
  const configRoutes = RouteBuilder.build(appConfig, {
    disabledRoutes: routeOptionsService.disabledRoutes
  });

  planYearResourceService.initializeSelectedPlanYear();

  // merging in dynamic routes on app load
  routes.unshift(...configRoutes);
  router.resetConfig(routes);
}

function initializePlanTypeDataMap(): void {
  const store = inject(Store);

  const appConfig = (environment.liveedit ? {} : config) as AppConfigModel;
  const planListTabsResource = (appConfig?.resourceBundle ?? []).reduce<Resource | undefined>(
    (found: Resource | undefined, bundle) => found || (bundle.resources ?? []).find(resource => resource.name === 'Spa_PlanList_Tabs_Data'),
    undefined
  );
  const planListTabs: TabData[] = planListTabsResource?.value ?? [];
  const activePlanTypes = [
    ...new Set(planListTabs.map((tab: TabData) => PlansUtilityService.convertToPlanType(tab.tabTypes[0] as PlanTypeAbbreviation)))
  ];

  if (activePlanTypes.length) {
    store.dispatch(new InitializePlanTypeDataMap(activePlanTypes));
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      DEFAULT_ROUTES,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig(ROUTER_CONFIG_OPTIONS),
      withInMemoryScrolling(IN_MEMORY_SCROLLING_OPTIONS)
    ),
    provideAppInitializer(() => {
      initializeAppRoutesAndSelectedPlanYear();
      initializePlanTypeDataMap();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideStore(
      [
        AgentState,
        ApplicationState,
        AppConfigState,
        ConfirmationState,
        RuntimeState,
        MemberState,
        DemographicState,
        ProfileState,
        PlansState,
        PharmacyState,
        ProviderState,
        EnrollmentState,
        MedicineCabinetState,
        PreferencesState,
        BlueButtonMedicineCabinetState,
        HelpfulToolsState,
        MedSupState,
        GuidedHelpState,
        MapState,
        UiState,
        ShoppingCartState,
        SOAState,
        QuoteBeneficiaryState,
        MillimanState,
        DeepLinkingParamsState,
        GoogleTagManagerState,
        JsonAnalyticsTagManagerState,
        LiveEditState,
        TwilioState,
        SiteState
      ],
      NGXS_CONFIG,
      withNgxsStoragePlugin({
        keys: ['application', 'ui', 'liveEdit'].sort(),
        storage: StorageOption.SessionStorage
      }),
      withVnextStorageLoggerPlugin(),
      withNgxsRouterPlugin()
    ),
    provideNgxMask(),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      NgbModule,
      SharedModule.forRoot(),
      CoreModule,
      NgIdleModule.forRoot(),
      VnextSharedModule
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthRequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthResponseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: PlanYearInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoginAgentIdInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'en-US' },
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) => platformLocation.getBaseHrefFromDOM(),
      deps: [PlatformLocation]
    },
    {
      provide: STORAGE_ENGINE,
      useClass: VNextStorageEngine
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    { provide: RouteReuseStrategy, useClass: VnextRouteReuseStrategy }
  ]
};
